import * as ko from 'knockout';

import { CodatDataType } from '@/features/accounting-software/types/CodatSync';
import { AccountingSoftwareIntegrationEventsServiceEventType } from '@/legacy/features/companies/accountingSoftware/analytics/helper';
import * as companyService from '@/legacy/services/company';
import CompanyEventService from '@/legacy/services/companyEventService';

import AccountingSoftwareCompany from '../types/AccountingSoftwareCompany';
import AccountingSoftwareContact from '../types/AccountingSoftwareContact';
import Bill from '../types/Bill';
import IAccount from '../types/IAccount';
import IAccountingSoftwareAddAccount from '../types/IAccountingSoftwareAddAccount';
import IAccountingSoftwarePackage from '../types/IAccountingSoftwarePackage';
import IBillAttachmentDetails from '../types/IBillAttachmentDetails';
import Invoice from '../types/Invoice';
import type { SupportedAccountingSoftwareType as AccountingSoftwareType } from '../types/SupportedAccountingSoftware';
import SyncType from '../types/SyncType';
import { getAccountingSoftwareNameByType } from '../utils/getAccountingSoftwareNameByType';
import { getAccountingSoftwareShortNameByType } from '../utils/getAccountingSoftwareShortNameByType';

const NoSyncProgress = ko.pureComputed(() => undefined);

abstract class BaseAccountingSoftwarePackage implements IAccountingSoftwarePackage {
  protected companyService: companyService.CompanyService;

  readonly id: AccountingSoftwareType;

  readonly name: string;

  readonly shortName: string;

  abstract readonly logoUrl: string;

  abstract readonly icon: string;

  abstract readonly supportsInvoices: boolean;

  readonly usesCodat: boolean = false;

  constructor(id: AccountingSoftwareType) {
    this.companyService = companyService.Instance;

    this.id = id;
    this.name = getAccountingSoftwareNameByType(id);
    this.shortName = getAccountingSoftwareShortNameByType(id);
  }

  getSyncProgress(_syncDataTypes: ReadonlyArray<CodatDataType>) {
    return NoSyncProgress;
  }

  abstract AddAccount(account: IAccountingSoftwareAddAccount): Promise<IAccount>;

  abstract Connect(): Promise<any>;

  abstract ConnectConnectIcon(): string;

  abstract Connected(info: any): Promise<void>;

  abstract FilterResolutions(conflictingAccountName: string): string;

  abstract GenerateConnectButton($target: JQuery): void;

  abstract GetAccounts(): Promise<Array<IAccount>>;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  GetBillAttachment(attachment: IBillAttachmentDetails) {}

  abstract GetBills(): Promise<Array<Bill>>;

  abstract fetchCompany(): Promise<AccountingSoftwareCompany>;

  abstract GetContacts(): Promise<Array<AccountingSoftwareContact>>;

  GetId(): AccountingSoftwareType {
    return this.id;
  }

  abstract GetInvoices(): Promise<Array<Invoice>>;

  GetName(): string {
    return this.name;
  }

  abstract GetReconciliationStatus(): Promise<string>;

  GetShortName(): string {
    return this.shortName;
  }

  SupportsInvoices(): boolean {
    return this.supportsInvoices;
  }

  SyncData(syncType: SyncType, syncDataTypes: ReadonlyArray<CodatDataType>): Promise<void> {
    return Promise.resolve();
  }

  async getFullVendorDetails(vendorDetails: any): Promise<any> {
    return vendorDetails;
  }

  async getFullCustomerDetails(customerDetails: any): Promise<any> {
    return customerDetails;
  }

  protected fireAccountingSoftwareConnectingEvent(context?: string) {
    CompanyEventService.emit(
      AccountingSoftwareIntegrationEventsServiceEventType.AccountingPlatformConnectionInitiated,
      {
        accountingPlatform: this.id,
        context,
      }
    );
  }

  protected fireAccountingSoftwareConnectedEvent() {
    CompanyEventService.emit(
      AccountingSoftwareIntegrationEventsServiceEventType.AccountingPlatformConnected,
      {
        accountingPlatform: this.id,
      }
    );
  }
}

export default BaseAccountingSoftwarePackage;
